import type { KeyboardEvent } from 'react';
import React, { useRef, useState, useLayoutEffect, useEffect } from 'react';
import {
    Button,
    useStyles,
    useTheme,
    Caption,
    Divider,
    pxToRem,
} from '@archipro-design/aria';
import * as S from './MobileShareBoardModal.style';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FormShape } from '@modules/design-board/component/share-modal/MobileShareBoardForm.types';
import {
    validator,
    emailsValidator,
} from '@modules/design-board/component/share-modal/MobileShareBoardForm.types';
import type { DesignBoardWithDetailedPinsFragment } from '../../../../../generated/graphql';
import FilterBarAvatars from '@modules/design-board/component/board-detail/FilterBarAvatars';
import { CollaboratorPermissionToggle } from '@modules/design-board/component/share-modal/CollaboratorPermissionToggle';
import { INPUT_ERROR_COLOR } from './MobileShareBoardModal.style';
import { useFetcher, useLocation } from '@remix-run/react';
import { MultiInputPills } from '@modules/design-board/component/share-modal/MultiInputPills';
import { SHOW_COMMENT } from '../../config/design-board-config';
import { useTracker } from '@archipro-website/tracker';
import { fetcherIsDone } from '~/utils/fetcherHelper';

interface ShareFormProps {
    board: DesignBoardWithDetailedPinsFragment;
    onClose: () => void;
}

export const MobileShareBoardForm = ({ board, onClose }: ShareFormProps) => {
    const { siteVariables } = useTheme();
    const [emailValidation, setEmailValidation] = useState<string>('');
    const { control, handleSubmit, watch, setValue } = useForm<FormShape>({
        resolver: zodResolver(validator),
        mode: 'onChange',
        defaultValues: {
            access: 'edit',
        },
    });

    const fetcher = useFetcher<FormShape>();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const tracker = useTracker();

    const emailFieldRef = useRef<HTMLInputElement>(null);
    useLayoutEffect(() => {
        if (emailFieldRef.current) {
            emailFieldRef.current.focus();
        }
    }, []);

    const { emails, access } = watch();
    const canSubmit = emails?.length > 0;
    const validEmail = emailValidation === '';
    const { css } = useStyles({ canSubmit, validEmail });

    const handleEmailBlur = () => {
        if (
            emailFieldRef.current &&
            emailFieldRef.current.value.trim() !== ''
        ) {
            const enterKeyEvent = new Event('keydown', { bubbles: true });
            Object.defineProperty(enterKeyEvent, 'keyCode', { get: () => 13 });
            emailFieldRef.current.dispatchEvent(enterKeyEvent);
        }
    };

    const handleKeyEmailUpdate = async (
        event: KeyboardEvent<HTMLInputElement>
    ) => {
        let value = event.currentTarget.value.trim();
        const delimitKeys = [13, 32, 188];
        if (delimitKeys.includes(event.keyCode)) {
            event.preventDefault();
            const enteredEmails = emails ?? [];
            const newEmails: string[] = [...enteredEmails, value];
            const result = emailsValidator.safeParse(newEmails);
            if (result.success) {
                setValue('emails', newEmails);
                setEmailValidation('');
                if (emailFieldRef.current) {
                    emailFieldRef.current.value = '';
                }
                return;
            }

            setEmailValidation(result.error.issues[0]?.message ?? '');
        }
    };

    const removeEmail = (email: string) => {
        if (emails.length > 0) {
            const newEmails: string[] = emails.filter((item) => item !== email);
            setValue('emails', newEmails);
        }
    };

    const submitShareForm = handleSubmit((data) => {
        setIsLoading(true);
        tracker.log('InviteCollaboratorsSubmit', {
            url: new URL(window.location.href),
            data: {
                ElementID:
                    'designboard_detail_header_inviteCollaboratorsReveal',
                Source: 'designBoard, detail',
            },
        });
        fetcher.submit(
            {
                email: data.emails.join(','),
                message: data.message || '',
                access: data.access,
                boardId: board.ID.toString(),
                _action: 'invite_collaborators',
            },
            {
                method: 'post',
                action: location.pathname,
            }
        );
    });

    useEffect(() => {
        if (fetcherIsDone(fetcher) && fetcher.data) {
            setIsLoading(false);
            onClose();
        }
    }, [fetcher, onClose]);

    return (
        <form
            id={`mobile_share_board_form_${board.ID}`}
            onSubmit={submitShareForm}
        >
            <div className={css(S.ModalFormFields)}>
                <Controller
                    control={control}
                    name={'emails'}
                    render={({ field }) => (
                        <div className={css(S.ModalFormField)} tabIndex={0}>
                            {emails && emails.length > 0 && (
                                <MultiInputPills
                                    inputs={emails}
                                    onRemove={(email) => removeEmail(email)}
                                />
                            )}
                            <input
                                ref={emailFieldRef}
                                type={'text'}
                                className={css(S.InputField(false))}
                                placeholder={'Enter email addresses'}
                                name={field.name}
                                onKeyDown={handleKeyEmailUpdate}
                                onBlur={handleEmailBlur}
                            />
                            {emailValidation && (
                                <Caption
                                    variant="04"
                                    variables={{
                                        captionColor: INPUT_ERROR_COLOR,
                                    }}
                                    className={css(S.InputError)}
                                >
                                    {emailValidation}
                                </Caption>
                            )}
                        </div>
                    )}
                />
                <Controller
                    control={control}
                    name={'message'}
                    render={({ field }) => (
                        <div className={css(S.ModalFormField)}>
                            <textarea
                                className={css(S.InputField(true))}
                                placeholder={'Enter message'}
                                name={field.name}
                                defaultValue={field.value}
                                onChange={field.onChange}
                            />
                        </div>
                    )}
                />
            </div>
            {canSubmit ? (
                <>
                    <CollaboratorPermissionToggle
                        bottomMenuData={{
                            type: 'permission',
                            canRemove: false,
                            canEdit: access === 'edit',
                            canView: access === 'view',
                            canComment: SHOW_COMMENT && access === 'comment',
                            changePermission: (permission) =>
                                permission && setValue('access', permission),
                        }}
                        permission={access}
                    />
                    <Button
                        className={css(S.ModalFormAction)}
                        color={'primary'}
                        type={'submit'}
                        size={14}
                        style={{
                            textTransform: 'uppercase',
                        }}
                        disabled={!validEmail}
                        loading={isLoading}
                        variables={{
                            primaryButtonBackgroundColorDisable:
                                siteVariables.colors.charcoal['100'],
                            buttonPadding: pxToRem(18),
                        }}
                    >
                        Invite
                    </Button>
                </>
            ) : (
                <>
                    <Divider
                        size={1}
                        className={css(S.CollaboratorsTopDivider)}
                        variables={{
                            dividerPrimaryColor:
                                siteVariables.colors.grey['100'],
                        }}
                    />
                    <Caption
                        variant="03"
                        className={css(S.CollaboratorsCaption)}
                    >
                        Manage collaborators
                    </Caption>
                    <FilterBarAvatars board={board} />
                </>
            )}
        </form>
    );
};
